import React, { useState, useEffect, useRef } from 'react';


const HairToDyeForTestimonials = ({ fontStyles }) => {
   const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);

  // Static fallback reviews for Hair To Dye For - at least 10 reviews
  const staticReviews = [
    {
      name: 'Nicole Sirianni-Klotz',
      rating: 5,
      text: "I can't say enough good things about them. Longevity is everything as I have been coming here over 10 years. All of the staff goes above and beyond for their clients. The owner constantly invests in the staff to keep them moving forward in their careers with knowledge and success!",
      date: 'a week ago'
    },
    {
      name: 'Sarah Mitchell',
      rating: 5,
      text: "Absolutely the best salon experience! My hair extensions look so natural and the color matching was perfect. The team really knows what they're doing and takes the time to understand exactly what you want.",
      date: '2 weeks ago'
    },
    {
      name: 'Jennifer Thompson',
      rating: 5,
      text: "I've been coming here for 5 years for hair coloring and extensions. The quality is consistently amazing and the staff is so professional. They truly care about the health of your hair and the results show!",
      date: '3 weeks ago'
    },
    {
      name: 'Maria Rodriguez',
      rating: 5,
      text: "Best hair transformation of my life! The balayage technique they used was flawless and my extensions blend perfectly. Worth every penny for the quality and expertise you get here.",
      date: 'a month ago'
    },
    {
      name: 'Ashley Chen',
      rating: 5,
      text: "The attention to detail is incredible. They took the time to understand my lifestyle and recommended the perfect low-maintenance color and style. I always leave feeling like a million bucks!",
      date: 'a month ago'
    },
    {
      name: 'Rebecca Davis',
      rating: 5,
      text: "Professional, talented, and genuinely caring staff. They fixed a botched color job from another salon and made my hair look better than ever. Can't recommend them enough!",
      date: '2 months ago'
    },
    {
      name: 'Emma Wilson',
      rating: 5,
      text: "The best salon in town! They transformed my damaged hair into something beautiful. The keratin treatment they recommended worked wonders. I'm so grateful for their expertise!",
      date: '2 months ago'
    },
    {
      name: 'Olivia Brown',
      rating: 5,
      text: "I've tried many salons but Hair To Dye For is exceptional. The atmosphere is welcoming, the staff is skilled, and they really listen to what you want. My highlights look amazing!",
      date: '3 months ago'
    },
    {
      name: 'Sophie Martinez',
      rating: 5,
      text: "From consultation to final result, everything was perfect. They took time to understand my hair type and lifestyle. The color correction they did was masterful. Highly recommend!",
      date: '3 months ago'
    },
    {
      name: 'Isabella Garcia',
      rating: 5,
      text: "Outstanding service and results! The team is incredibly knowledgeable about the latest techniques. My hair has never looked or felt better. This is my forever salon!",
      date: '4 months ago'
    }
  ];

  useEffect(() => {
    // Initialize with static reviews
    setReviews(staticReviews);
    
    // Try to fetch real Google reviews
    fetchGoogleReviews();
  }, []);

  // Auto-play carousel - rotate every 5 seconds
  useEffect(() => {
    if (isAutoPlaying && reviews.length > 2) {
      intervalRef.current = setInterval(() => {
        handleNext();
      }, 5000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [currentIndex, isAutoPlaying, reviews.length]);

  const fetchGoogleReviews = async () => {
    // Check localStorage cache
    const cacheKey = 'hair-to-dye-for-reviews-cache';
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      try {
        const { reviews: cachedReviews, timestamp } = JSON.parse(cached);
        const hoursSinceCache = (Date.now() - timestamp) / (1000 * 60 * 60);
        
        if (hoursSinceCache < 24 && cachedReviews.length > 0) {
          setReviews(cachedReviews);
          return;
        }
      } catch (e) {
        console.log('Cache parse error:', e);
      }
    }

    // Try fetching with CORS proxies
    const proxies = [
      'https://api.allorigins.win/raw?url=',
      'https://corsproxy.io/?'
    ];

    let fetchedReviews = [];
    
    for (const proxy of proxies) {
      try {
        const googleUrl = 'https://www.google.com/search?q=Hair+To+Dye+For+Reviews';
        const response = await fetch(proxy + encodeURIComponent(googleUrl));

        if (response.ok) {
          const html = await response.text();
          fetchedReviews = parseReviewsFromHTML(html);
          
          if (fetchedReviews.length > 0) {
            // Ensure we have at least 10 reviews by combining with static
            const combinedReviews = [...fetchedReviews, ...staticReviews];
            const uniqueReviews = Array.from(
              new Map(combinedReviews.map(r => [r.name, r])).values()
            ).slice(0, 10);
            
            // Cache the results
            localStorage.setItem(cacheKey, JSON.stringify({
              reviews: uniqueReviews,
              timestamp: Date.now()
            }));
            setReviews(uniqueReviews);
            break;
          }
        }
      } catch (error) {
        console.log(`Proxy ${proxy} failed:`, error);
      }
    }

    // If no reviews fetched, keep using static ones
    if (fetchedReviews.length === 0) {
      console.log('Using static reviews as fallback');
    }
  };

  const parseReviewsFromHTML = (html) => {
    const reviews = [];
    
    try {
      // Look for structured data in the HTML
      const jsonLdRegex = /<script type="application\/ld\+json">(.*?)<\/script>/gs;
      const matches = html.matchAll(jsonLdRegex);
      
      for (const match of matches) {
        try {
          const data = JSON.parse(match[1]);
          if (data.review || (data['@graph'] && data['@graph'][0]?.review)) {
            const reviewArray = data.review || data['@graph'][0].review;
            reviewArray.forEach(r => {
              if (r.reviewRating?.ratingValue >= 4) {
                reviews.push({
                  name: r.author?.name || 'Google User',
                  rating: parseInt(r.reviewRating.ratingValue),
                  text: r.reviewBody || r.description,
                  date: r.datePublished || 'Recently'
                });
              }
            });
          }
        } catch (e) {
          // Continue to next match
        }
      }
    } catch (error) {
      console.error('Error parsing reviews:', error);
    }

    // Return only 5-star reviews, limited to 10
    return reviews.filter(r => r.rating === 5).slice(0, 10);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => {
      // Move back by 2 reviews
      if (prevIndex === 0) {
        // If at start, go to last pair
        return Math.max(0, reviews.length - 2);
      } else if (prevIndex === 1) {
        // Special case for odd number of reviews
        return 0;
      } else {
        return prevIndex - 2;
      }
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      // Move forward by 2 reviews
      if (prevIndex >= reviews.length - 2) {
        // If at end, go back to start
        return 0;
      } else {
        return prevIndex + 2;
      }
    });
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index * 2);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const getVisibleReviews = () => {
    if (reviews.length === 0) return [];
    
    // Always show 2 reviews
    const visible = [];
    for (let i = 0; i < 2; i++) {
      if (currentIndex + i < reviews.length) {
        visible.push(reviews[currentIndex + i]);
      } else {
        // If we don't have enough reviews, wrap around
        visible.push(reviews[(currentIndex + i) % reviews.length]);
      }
    }
    return visible;
  };

  const visibleReviews = getVisibleReviews();
  const totalDots = Math.ceil(reviews.length / 2);

  return (
    <>
      {/* Testimonial Cards - Always show 2 reviews */}
      <div className="mt-8 lg:mt-16 mx-auto max-w-7xl">
        <div className="flex gap-4 lg:gap-5 px-4">
          {visibleReviews.map((review, index) => (
            <div
              key={`${currentIndex}-${index}`}
              className="flex-1"
              style={{
                display: 'flex',
                padding: '40px 20px',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '48px',
                flex: '1 0 0',
                border: '1px solid #252525',
                background: 'rgba(255, 255, 255, 0.01)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 italic text-center">
                "{review.text}"
              </p>
              <div className="text-center">
                <p className="text-white font-semibold text-sm sm:text-base uppercase tracking-widest mb-2">
                  {review.name}
                </p>
                <div className="flex justify-center text-[#D4AF37] text-lg">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star}>★</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows - Optional, positioned outside cards */}
        {reviews.length > 2 && (
          <>
            <button
              onClick={() => {
                handlePrevious();
                setIsAutoPlaying(false);
                setTimeout(() => setIsAutoPlaying(true), 10000);
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-20 bg-black/30 hover:bg-black/50 rounded-full p-2 transition-all duration-300"
              aria-label="Previous reviews"
              style={{ display: 'none' }} // Hidden by default, remove this line if you want arrows
            >
              <svg className="h-5 w-5 text-[#F2EADC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => {
                handleNext();
                setIsAutoPlaying(false);
                setTimeout(() => setIsAutoPlaying(true), 10000);
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-20 bg-black/30 hover:bg-black/50 rounded-full p-2 transition-all duration-300"
              aria-label="Next reviews"
              style={{ display: 'none' }} // Hidden by default, remove this line if you want arrows
            >
              <svg className="h-5 w-5 text-[#F2EADC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
        
        {/* Pagination Dots */}
        <div className="flex justify-center mt-8 gap-2">
          {[...Array(Math.min(totalDots, 5))].map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                Math.floor(currentIndex / 2) === index 
                  ? 'bg-[#F2EADC]' 
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to review set ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Leave a Google Review Button */}
     <div className="mt-10 mb-8 sm:mb-12 lg:mb-16 text-center">
        <a
          href="https://www.google.com/search?client=firefox-b-d&sca_esv=81be61c3dad309e5&sxsrf=AE3TifN0IC6GGwnzGVh1j8O2HymqJy0lyg:1758198692618&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8symjwf52D61GHS9EuAe_NORTNlwArz7JtwUY-RxfvoaPQbVtwHu9WvlnWOgh6_Uk6Eqg-PkT3ThsOMNZK75_E6mD2oU9&q=Hair+To+Dye+For+Reviews&sa=X&ved=2ahUKEwi-oJLEqOKPAxVhTWwGHTiOCo0Q0bkNegQIIhAE&biw=1920&bih=878&dpr=1#lrd=0x89c46c53d289254b:0xfd5569212d3599cf,3,,,,"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#F2EADC] text-black hover:bg-white px-6 py-3 rounded-full transition-all duration-300 font-semibold"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#F2EADC"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Leave a Google Review
        </a>
      </div>
    </>
  );
};
export default HairToDyeForTestimonials;
